/**
 * useThreeDSecureBooking: books an appointment with Stripe 3D Secure (SCA)
 * challenge handling. Wraps the unchanged bookAppointment service. It always
 * opts into the server 3DS contract; if the bank requires a challenge, it runs
 * stripe.handleNextAction with the returned client secret and re-books with the
 * authenticated PaymentIntent.
 *
 * 3DS is not opt-in behaviour we choose per booking: Stripe requests a challenge
 * only when the issuer or card network mandates one (request_three_d_secure is
 * 'automatic'). Gating our side of that contract behind a flag meant a booking
 * the bank had already decided needed authentication was rejected with a generic
 * "re-enter your payment method" error the customer could not act on, so the flag
 * is gone and support is unconditional.
 *
 * Reports the challenge to analytics as 'presented' and then one of 'completed',
 * 'declined' (the issuer refused the card) or 'failed' (authentication itself did
 * not succeed) — see classify3DSOutcome for why those last two are kept apart.
 *
 * Returns a stable-signature async function so callers thread no Stripe
 * instance. See docs/superpowers/specs/2026-07-27-3dsecure-web-design.md.
 */
import { useStripe } from '@stripe/react-stripe-js'
import { bookAppointment } from '../services/GraphQlClientService'
import { trackBookingChallenge } from './tracking'
import {
  parse3DSError,
  classify3DSOutcome,
  ThreeDSError,
  THREE_DS_FAILED_MESSAGE,
  THREE_DS_DECLINED_MESSAGE,
} from './threeDSecure'

/**
 * @returns {(appointment: object, appointmentAddress: object, marketShortName?: string) => Promise<object>}
 *   a function that books the appointment, running a 3DS challenge if required,
 *   and resolves to the booked appointment.
 */
export const useThreeDSecureBooking = () => {
  const stripe = useStripe()

  return async (appointment, appointmentAddress, marketShortName) => {
    const appt = { ...appointment, supports3DSecure: true }
    try {
      return await bookAppointment(appt, appointmentAddress)
    } catch (err) {
      const challenge = parse3DSError(err)
      if (!challenge) {
        throw err
      }

      const ctx = {
        paymentIntentId: challenge.paymentIntentId,
        market: marketShortName,
        paymentMethod: 'card',
      }
      trackBookingChallenge('presented', ctx)

      if (!stripe) {
        trackBookingChallenge('failed', { ...ctx, reason: 'stripe_unavailable' })
        throw new ThreeDSError(THREE_DS_FAILED_MESSAGE, 'stripe_unavailable')
      }

      let result
      try {
        result = await stripe.handleNextAction({
          clientSecret: challenge.paymentIntentClientSecret,
        })
      } catch {
        trackBookingChallenge('failed', { ...ctx, reason: 'handle_next_action_threw' })
        throw new ThreeDSError(THREE_DS_FAILED_MESSAGE, 'handle_next_action_threw')
      }

      // 'declined' and 'failed' are reported separately: an issuer decline is a
      // card problem, not an authentication problem, and counting it as a 3DS
      // failure is what made the funnel unreadable. See classify3DSOutcome.
      const { outcome, reason, declineCode } = classify3DSOutcome(result)

      if (outcome !== 'completed') {
        trackBookingChallenge(outcome, {
          ...ctx,
          reason,
          ...(declineCode ? { declineCode } : {}),
        })
        throw new ThreeDSError(
          outcome === 'declined' ? THREE_DS_DECLINED_MESSAGE : THREE_DS_FAILED_MESSAGE,
          reason,
          declineCode,
        )
      }

      trackBookingChallenge('completed', ctx)
      return await bookAppointment(
        {
          ...appt,
          is3DSecureRetry: true,
          paymentIntentId: challenge.paymentIntentId,
        },
        appointmentAddress,
      )
    }
  }
}
